// assets/byte-discount-price.js

if (!customElements.get('byte-discount-price')) {
  class ByteDiscountPrice extends HTMLElement {
    static PLACEHOLDER_PATTERN = /\{\{\s*(\w+)\s*\}\}/;

    constructor() {
      super();

      this.priceObserver = null;
      this.syncFrame = null;
      this.boundHandlePriceChange = this.handlePriceChange.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      if (!this.priceSource || !this.valueElement) return;

      this.#observePriceSource();
      this.#syncPrice();
    }

    disconnectedCallback() {
      this.priceObserver?.disconnect();
      window.cancelAnimationFrame(this.syncFrame);
    }

    handlePriceChange() {
      // Aris and Dawn can rewrite the price several times per change; sync once per frame
      window.cancelAnimationFrame(this.syncFrame);
      this.syncFrame = window.requestAnimationFrame(() => this.#syncPrice());
    }

    #cacheElements() {
      this.priceSource = document.querySelector(this.dataset.priceSource);
      this.valueElement = this.querySelector('[data-discount-price-value]');
      this.discountPercentage = Number(this.dataset.discountPricePercentage) || 0;
      this.moneyFormat = this.dataset.discountPriceMoneyFormat || '${{amount}}';
    }

    #observePriceSource() {
      // Dawn replaces the price block's children on variant change; Aris edits the text in place
      this.priceObserver = new MutationObserver(this.boundHandlePriceChange);
      this.priceObserver.observe(this.priceSource, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    #syncPrice() {
      const currentPriceCents = this.#getDisplayedPriceCents();
      if (currentPriceCents === null) return;

      const discountedPriceCents = Math.round((currentPriceCents * (100 - this.discountPercentage)) / 100);
      const discountedPriceText = this.#formatMoney(discountedPriceCents);

      if (this.valueElement.textContent !== discountedPriceText) {
        this.valueElement.textContent = discountedPriceText;
      }
    }

    #getDisplayedPriceCents() {
      // Same source of truth as byte-sticky-price-bar: Aris clones the theme price into
      // `.apo-variant-price` and writes the option-adjusted total into `.price-item--sale`.
      const priceContainer =
        this.priceSource.querySelector('.price__container.apo-variant-price') ||
        this.priceSource.querySelector('.price__container');
      if (!priceContainer) return null;

      const salePrice = priceContainer.querySelector('.price__sale .price-item--sale');
      const regularPrice = priceContainer.querySelector('.price__regular .price-item--regular');
      const priceText = salePrice?.textContent.trim() || regularPrice?.textContent.trim() || '';

      return this.#parseMoney(priceText);
    }

    #parseMoney(priceText) {
      const digits = priceText.replace(/[^\d]/g, '');
      if (!digits) return null;

      const priceValue = Number(digits);
      const hasDecimals = !/no_decimals/.test(this.moneyFormat);

      return hasDecimals ? priceValue : priceValue * 100;
    }

    #formatMoney(priceCents) {
      const formatMatch = this.moneyFormat.match(ByteDiscountPrice.PLACEHOLDER_PATTERN);
      if (!formatMatch) return String(priceCents / 100);

      const formattedAmount = this.#formatAmount(priceCents, formatMatch[1]);

      return this.moneyFormat.replace(ByteDiscountPrice.PLACEHOLDER_PATTERN, formattedAmount);
    }

    #formatAmount(priceCents, formatKey) {
      const formatOptions = {
        amount: { decimals: 2, thousands: ',', decimal: '.' },
        amount_no_decimals: { decimals: 0, thousands: ',', decimal: '.' },
        amount_with_comma_separator: { decimals: 2, thousands: '.', decimal: ',' },
        amount_no_decimals_with_comma_separator: { decimals: 0, thousands: '.', decimal: ',' },
        amount_with_apostrophe_separator: { decimals: 2, thousands: "'", decimal: '.' },
        amount_with_space_separator: { decimals: 2, thousands: ' ', decimal: ',' },
      };
      const { decimals, thousands, decimal } = formatOptions[formatKey] || formatOptions.amount;

      const [integerPart, fractionPart] = (priceCents / 100).toFixed(decimals).split('.');
      const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

      return fractionPart ? `${groupedInteger}${decimal}${fractionPart}` : groupedInteger;
    }
  }

  customElements.define('byte-discount-price', ByteDiscountPrice);
}
